import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import { getLocations } from '../../../services/LocationService';
import { getLogo } from '../../../services/LogoStorageService';
import CampaignService from '../../../services/CampaignService';
import SnackbarUtils from '../../../utils/SnackbarUtils';
import { getIcon } from '../../../utils/IconHandler';
import { formatMoney } from '../../../utils/currency';

const MINIMUM_SPEND_PLACEHOLDER = '{monto_minimo}';

// Mismos textos que el backend usa cuando un campo queda vacío.
const DEFAULT_TEXTS = {
  ticketTitle: 'Beneficio para tu próxima compra',
  ticketDescription: 'En tu próxima compra por nuestra web o en el local podés acceder a promociones exclusivas.',
  ticketSubdescription: `Comprando desde ${MINIMUM_SPEND_PLACEHOLDER} ganás beneficios exclusivos para tu próxima compra.`,
  ticketRewardText: 'Podés recibir descuentos, regalos o premios especiales según la promo disponible.',
  ticketFooter: 'Comprá directo y aprovechá más beneficios.'
};

const emptyCampaign = () => ({
  enabled: false,
  locationIds: [],
  startDate: '',
  endDate: '',
  minimumSpend: 0,
  emissionPercentage: 100,
  ...DEFAULT_TEXTS
});

const normalizeCampaign = (campaign) => {
  const base = emptyCampaign();
  if (!campaign) return base;
  return {
    ...base,
    ...campaign,
    startDate: campaign.startDate || '',
    endDate: campaign.endDate || '',
    ticketTitle: campaign.ticketTitle ?? base.ticketTitle,
    ticketDescription: campaign.ticketDescription ?? base.ticketDescription,
    ticketSubdescription: campaign.ticketSubdescription ?? base.ticketSubdescription,
    ticketRewardText: campaign.ticketRewardText ?? base.ticketRewardText,
    ticketFooter: campaign.ticketFooter ?? base.ticketFooter
  };
};

const formatMinimumSpend = (value) => {
  const amount = Number(value) || 0;
  return amount <= 0 ? 'cualquier monto' : `${formatMoney(Math.round(amount))}`;
};

const TicketPreview = ({ campaign, logoUrl }) => {
  const fill = (text) => (text || '').split(MINIMUM_SPEND_PLACEHOLDER).join(formatMinimumSpend(campaign.minimumSpend));
  const separator = <Typography sx={{ fontSize: 11, fontFamily: 'monospace', my: 1 }}>{'-'.repeat(34)}</Typography>;

  return (
    <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'center' } }}>
      <Box
        sx={{
          width: 250,
          maxWidth: '100%',
          bgcolor: '#fff',
          color: '#111',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          px: 1.5,
          py: 2,
          fontFamily: 'Arial, sans-serif',
          textAlign: 'center'
        }}
      >
        {logoUrl ? (
          <Box component="img" src={logoUrl} alt="Logo" sx={{ display: 'block', maxWidth: 110, maxHeight: 52, objectFit: 'contain', mx: 'auto', mb: 2 }} />
        ) : (
          <Typography sx={{ fontFamily: 'serif', fontSize: 28, lineHeight: 1, mb: 2 }}>Empresa</Typography>
        )}
        <Typography sx={{ fontSize: 18, fontWeight: 700 }}>{fill(campaign.ticketTitle)}</Typography>
        <Typography sx={{ fontSize: 11 }}>Ticket N°: 28944</Typography>
        <Typography sx={{ fontSize: 11 }}>Fecha: 22/07/2026 18:14</Typography>
        {separator}
        {campaign.ticketDescription && <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 0.75 }}>{fill(campaign.ticketDescription)}</Typography>}
        {campaign.ticketSubdescription && <Typography sx={{ fontSize: 13, mb: 1.5 }}>{fill(campaign.ticketSubdescription)}</Typography>}
        {campaign.ticketRewardText && <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 1 }}>{fill(campaign.ticketRewardText)}</Typography>}
        {campaign.ticketFooter && (
          <>
            {separator}
            <Typography sx={{ fontSize: 12 }}>{fill(campaign.ticketFooter)}</Typography>
          </>
        )}
      </Box>
    </Box>
  );
};

const PedidosYaCampaignTab = () => {
  const [locations, setLocations] = useState([]);
  const [formData, setFormData] = useState(emptyCampaign());
  const [logoUrl, setLogoUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const selectedLocationsText = useMemo(() => {
    if (!formData.locationIds.length) return 'Todas las sucursales';
    return locations
      .filter((location) => formData.locationIds.includes(Number(location.id)))
      .map((location) => location.name)
      .join(', ');
  }, [formData.locationIds, locations]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        setLoading(true);
        const [campaign, locationsResponse] = await Promise.all([
          CampaignService.getPedidosYaCampaign(),
          getLocations()
        ]);
        if (!mounted) return;
        setLocations(locationsResponse || []);
        setFormData(normalizeCampaign(campaign));
      } catch (err) {
        if (mounted) setError('Error al cargar la campaña PedidosYa');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    getLogo().then((logo) => mounted && setLogoUrl(logo || null)).catch(() => mounted && setLogoUrl(null));
    return () => {
      mounted = false;
    };
  }, []);

  const updateField = (field, value) => setFormData((prev) => ({ ...prev, [field]: value }));

  const toggleLocation = (locationId) => {
    const id = Number(locationId);
    setFormData((prev) => ({
      ...prev,
      locationIds: prev.locationIds.includes(id)
        ? prev.locationIds.filter((current) => current !== id)
        : [...prev.locationIds, id]
    }));
  };

  const save = async () => {
    const percentage = Number(formData.emissionPercentage);
    if (percentage < 0 || percentage > 100) {
      setError('El porcentaje de salida debe estar entre 0 y 100.');
      return;
    }
    if (Number(formData.minimumSpend) < 0) {
      setError('El monto mínimo no puede ser negativo.');
      return;
    }
    if (formData.startDate && formData.endDate && formData.endDate < formData.startDate) {
      setError('La fecha de fin no puede ser anterior a la de inicio.');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      const saved = await CampaignService.savePedidosYaCampaign({
        ...formData,
        minimumSpend: Number(formData.minimumSpend) || 0,
        emissionPercentage: Number.isFinite(percentage) ? percentage : 100,
        startDate: formData.startDate || null,
        endDate: formData.endDate || null
      });
      setFormData(normalizeCampaign(saved));
      SnackbarUtils.success('Campaña PedidosYa guardada');
    } catch (err) {
      const message = err.response?.data?.message || err.response?.data?.error || err.message || 'Error al guardar PedidosYa';
      setError(message);
      SnackbarUtils.error(message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Paper sx={{ p: 3, mt: 2 }}>Cargando...</Paper>;

  return (
    <Paper sx={{ p: 3, mt: 2 }}>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h6">Campaña PedidosYa</Typography>
          <Typography variant="body2" color="text.secondary">
            Si se activa, aplica únicamente a las ventas de PedidosYa. Cuando la venta cumple los requisitos se imprime un ticket promocional para invitar al cliente a comprar directo.
          </Typography>
        </Box>
        <FormControlLabel
          control={<Switch checked={formData.enabled} onChange={(e) => updateField('enabled', e.target.checked)} />}
          label="Habilitar PedidosYa"
        />
      </Box>
      <Divider sx={{ mb: 2 }} />

      {formData.enabled && (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Requisitos</Typography>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField fullWidth type="number" label="Monto mínimo" value={formData.minimumSpend} onChange={(e) => updateField('minimumSpend', e.target.value)} helperText="La venta debe alcanzar este total para imprimir el ticket." />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField fullWidth type="number" label="% de salida" value={formData.emissionPercentage} onChange={(e) => updateField('emissionPercentage', e.target.value)} helperText="Chance de imprimir el ticket en ventas elegibles." />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField fullWidth type="date" label="Inicio" InputLabelProps={{ shrink: true }} value={formData.startDate} onChange={(e) => updateField('startDate', e.target.value)} helperText="Opcional. Desde cuándo se evalúa." />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField fullWidth type="date" label="Fin" InputLabelProps={{ shrink: true }} value={formData.endDate} onChange={(e) => updateField('endDate', e.target.value)} helperText="Opcional. Hasta cuándo se evalúa." />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2">Sucursales</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Si no seleccionás ninguna, aplica en todas. Actual: {selectedLocationsText}</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {locations.map((location) => (
                <FormControlLabel
                  key={location.id}
                  control={<Checkbox checked={formData.locationIds.includes(Number(location.id))} onChange={() => toggleLocation(location.id)} />}
                  label={location.name}
                />
              ))}
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Grid container spacing={3} alignItems="flex-start">
              <Grid item xs={12} md={7}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Contenido del ticket</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Textos que se imprimen en el ticket. Podés usar {MINIMUM_SPEND_PLACEHOLDER} para mostrar el monto mínimo configurado. Si dejás un campo vacío se usa el texto por defecto.
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField fullWidth label="Título" value={formData.ticketTitle} onChange={(e) => updateField('ticketTitle', e.target.value)} helperText="Encabezado del ticket." />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth multiline minRows={2} label="Descripción" value={formData.ticketDescription} onChange={(e) => updateField('ticketDescription', e.target.value)} helperText="Mensaje principal, en negrita." />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth multiline minRows={2} label="Subdescripción" value={formData.ticketSubdescription} onChange={(e) => updateField('ticketSubdescription', e.target.value)} helperText="Línea secundaria debajo de la descripción." />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth multiline minRows={2} label="Beneficio destacado" value={formData.ticketRewardText} onChange={(e) => updateField('ticketRewardText', e.target.value)} helperText="Texto del beneficio, en negrita." />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth label="Texto final" value={formData.ticketFooter} onChange={(e) => updateField('ticketFooter', e.target.value)} helperText="Cierre del ticket." />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} md={5}>
                <Typography variant="subtitle2" align="center" sx={{ mb: 2 }}>Vista previa</Typography>
                <TicketPreview campaign={formData} logoUrl={logoUrl} />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        <Button variant="contained" startIcon={getIcon('save')} onClick={save} disabled={saving}>
          {saving ? 'Guardando...' : 'Guardar'}
        </Button>
      </Box>
    </Paper>
  );
};

export default PedidosYaCampaignTab;
